<template>
  <body style="background-color: grey; width: 100%; position: fixed; left: 0; top: 0">
    <div>
      <p style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%)"></p>
    </div>
    <div v-if="isVisible" class="widget" :style="{ height: isMinimized ? '48.5px' : isExpanded ? '100vh' : '176px' }">
      <header title="Click to minimize survey" @click="toggleMinimized()">
        <h1 v-if="!snapshotTitle">{{ widgetTitle }}</h1>
        <h1 v-if="snapshotTitle">{{ currentTitle }}</h1>
        <button class="closeButton" @click="toggleVisibility">X</button>
      </header>
      <div class="container" :style="{ height: isMinimized ? '48.5px' : isExpanded ? '100vh' : '176px' }">
        <div v-if="ratingsBarVisible">
          <div v-if="!isLoadingLocations">
            <h1>{{ $t('message.dine_experience') }}</h1>
            <WRatingsBar
              section-tag="ratings-widget-overall"
              v-bind="{ useFaces: false, useColors: false, useBorder: false, size: '40px' }"
              v-model="rating"
              @update:model-value="handleOverallRating"
            />
          </div>
          <div v-else>
            <h1>Please wait while we retrieve locations.</h1>
            <div class="spinner-container">
              <div class="loading-spinner"></div>
            </div>
          </div>
        </div>
        <div v-else-if="choosingLocation">
          <div class="location-search fadeIn">
            <div class="search">
              <input
                id="filter"
                v-model="filterText"
                class="filter"
                type="text"
                :placeholder="$t('message.filter_placeholder')"
              />
            </div>
          </div>
          <ul id="locationlist" class="location-list">
            <li
              v-for="location in getLocations"
              :key="location.id"
              class="location-button"
              @click="handleLocation(location)"
            >
              <div class="address">
                <div class="label">{{ location.label }}</div>
                <div>{{ location.address.addressLineOne }}</div>
                <div>{{ location.address.addressLineTwo }}</div>
              </div>
            </li>
          </ul>
          <div class="controls">
            <button class="cancel-button" @click="toggleVisibility">Cancel</button>
          </div>
        </div>
        <div v-else-if="answeringQuestionnaire">
          <div class="questionnaire-header">
            <h2>{{ $t('message.questionnaire_location') }}</h2>
          </div>
          <div class="location-container">
            <header class="chosen-address">
              <div class="location-title">{{ selectedLocation.label }}</div>
              <div>{{ selectedLocation.address.addressLineOne }}</div>
              <div>{{ selectedLocation.address.addressLineTwo }}</div>
            </header>
            <button class="change-location" @click="toggleChangeLocation">
              {{ $t('message.questionnaire_change') }}
            </button>
          </div>
          <div>
            <div class="questionnaire-header">
              <h2>{{ $t('message.questionnaire_time') }}</h2>
            </div>
            <div class="pickers">
              <div class="date-picker">
                <button class="arrow-button" @click="changeDate(-1)">❮</button>
                <div class="picker">{{ formatDate(selectedDate) }}</div>
                <button class="arrow-button" :disabled="isCurrentDate(selectedDate)" @click="changeDate(1)">❯</button>
              </div>
              <div class="time-picker">
                <button class="arrow-button" @click="changeTime(-1)">❮</button>
                <div class="picker">{{ formatTime(previousTime) }} - {{ formatTime(selectedTime) }}</div>
                <button class="arrow-button" :disabled="isCurrentHour(selectedTime)" @click="changeTime(1)">❯</button>
              </div>
            </div>
            <div class="questionnaire-header">
              <h2>{{ $t('message.questionnaire_experience') }}</h2>
            </div>
            <form class="questionnaire-list">
              <div v-for="question in getQuestions" :key="question.title">
                <label v-if="!question.requireCaptcha" class="selection">
                  <div class="questionnaire-location">
                    <div class="promo-containter">
                      <input
                        :id="question.id"
                        v-model="questionnaireChoice"
                        class="question-button"
                        type="radio"
                        name="radio"
                        :value="question.title"
                        @change="handleQuestionnaire(question)"
                      />
                      <div class="question-label">{{ question.title }}</div>
                      <div v-if="question && question.reward" class="reward-label">{{ question.reward.label }}</div>
                    </div>
                  </div>
                </label>
              </div>
            </form>
            <div v-if="selectedQuestionnaire && selectedQuestionnaire.requiresEmail !== 'disabled'">
              <div class="questionnaire-header">
                <h2>{{ $t('message.questionnaire_email') }}</h2>
              </div>
              <div class="pickers">
                <input
                  v-model="userEmail"
                  class="email-field"
                  type="text"
                  :placeholder="$t('message.email_placeholder')"
                />
              </div>
            </div>
            <div class="wrapper">
              <div class="questionnaire-error" :hidden="hasError">
                {{ $t('message.questionnaire_errors', { errorList: questionnaireErrors }) }}
              </div>
              <button class="submit" style="color: white" @click="submitQuestionnaire">
                {{ $t('message.questionnaire_next') }}
              </button>
            </div>
          </div>
        </div>
        <div v-else-if="answeringValidation">
          <div class="questionnaire-header">
            <h1>Enter the 6-digit code we sent to your email address to verify.</h1>
          </div>
          <div class="validation">
            <v-otp-input v-model="verificationCode" length="6" variant="underlined"></v-otp-input>
            <h2>
              You’ll have 5 chances to enter your code and 1 hour before it expires. Can’t find it? Please check your
              spam folder.
            </h2>
            <a class="resend-link" @click="resendVerification">{{ resendCode }}</a>
            <div class="snapshot-error" :hidden="verificationError">
              Invalid code. Please try again in {{ verificationSeconds }} seconds. You have
              {{ remainingAttempts }} attempts remaining.
            </div>
          </div>
          <div class="questionnaire-footer">
            <button class="snapshot-return" @click="cancelValidation">Go Back</button>
            <button class="snapshot-next" :disabled="!verificationError" @click="submitVerification">
              {{ 'Next' }}
            </button>
          </div>
        </div>

        <div v-else-if="answeringSnapshots && currentSnapshot">
          <div class="questionnaire-list">
            <div class="questionnaire-snapshot">
              <h2 class="snapshot-header">{{ currentSnapshot.snapshot.questionText }}</h2>
              <WRatingsBar
                section-tag="ratings-widget-snapshot"
                v-bind="{ useFaces: false, useColors: false, useBorder: false, size: '40px' }"
                v-model="snapshotRatingBars[currentSnapshot.id]"
                @update:model-value="handleRatingChange"
              />
              <button
                :class="{
                  'questionnaire-skippable': currentSnapshot.optional,
                  'questionnaire-unskippable': !currentSnapshot.optional,
                }"
                :disabled="!currentSnapshot.optional"
                @click="submitSnapshot(currentSnapshot.snapshotId, -1)"
              >
                Skip this snapshot
              </button>
            </div>
            <div class="questionnaire-header">
              <h2>Which factors had a positive or negative impact on your star rating?</h2>
            </div>

            <div v-for="reason in currentSnapshot.reasons" :key="reason.reason.id" class="snapshot-reasons">
              <p class="snapshot-label">{{ reason.reason.label }}</p>
              <div class="face-rating">
                <v-icon
                  :icon="mdiEmoticonSadOutline"
                  class="negative-impact"
                  :class="{ negativeselected: snapshotRatingBars[reason.reason.id] === -1 }"
                  size="50px"
                  :value="-1"
                  @click="setSnapshotRating(reason.reasonId, -1, currentSnapshot.snapshot.id)"
                ></v-icon>
                <v-icon
                  :icon="mdiEmoticonNeutralOutline"
                  class="neutral-impact"
                  :class="{ neutralselected: snapshotRatingBars[reason.reason.id] === 0 }"
                  size="50px"
                  :value="0"
                  @click="setSnapshotRating(reason.reasonId, 0, currentSnapshot.snapshot.id)"
                ></v-icon>
                <v-icon
                  :icon="mdiEmoticonHappyOutline"
                  class="positive-impact"
                  :class="{ positiveselected: snapshotRatingBars[reason.reason.id] === 1 }"
                  size="50px"
                  :value="1"
                  @click="setSnapshotRating(reason.reasonId, 1, currentSnapshot.snapshot.id)"
                ></v-icon>
              </div>
            </div>
            <div class="questionnaire-header">
              <h2>Comments on the {{ currentSnapshot.snapshot.label }}</h2>
            </div>
            <div class="questionnaire-snapshot">
              <textarea
                v-model="snapshotComments[currentSnapshot.snapshotId]"
                class="snapshot-text"
                placeholder="My experience was..."
                @input="validateComment"
              />
              <div v-if="validationMessages[currentSnapshot.snapshotId]" class="snapshot-error">
                {{ validationMessages[currentSnapshot.snapshotId] }}
              </div>
            </div>
            <div class="questionnaire-footer">
              <button class="snapshot-return" @click="previousSnapshot">Go Back</button>
              <button
                class="snapshot-next"
                @click="submitSnapshot(currentSnapshot.snapshotId, snapshotRatingBars[currentSnapshot.id])"
              >
                {{ 'Next' }}
              </button>
            </div>
          </div>
        </div>
        <div v-else-if="answeringAdditionalQuestions">
          <div v-for="question in getSnapshots.questions" :key="question.id">
            <div class="questionnaire-header">
              <h2>{{ question.question }}</h2>
            </div>
            <div v-if="question.fieldType === 'radio'">
              <ul class="additional-questions">
                <li v-for="answer in question.answers" :key="answer.id" class="additional-list">
                  <label class="additional-label"
                    ><input
                      :id="answer.id"
                      :name="question.id"
                      :value="answer.weight"
                      type="radio"
                      class="additional-radio"
                      :data-answer-title="answer.title"
                    />{{ answer.title }}</label
                  >
                </li>
              </ul>
            </div>
            <div v-if="question.fieldType === 'textarea'">
              <div class="questionnaire-snapshot">
                <textarea v-model="snapshotComment" :name="question.id" class="snapshot-text"></textarea>
              </div>
            </div>
          </div>
          <div class="questionnaire-footer">
            <div class="questionnaire-error" :hidden="feedbackAlreadyReceived">
              There was an error submitting your survey.
            </div>
            <button class="snapshot-return" @click="previousSnapshot">Go Back</button>
            <button class="snapshot-next" :disabled="isSubmitting" @click="submitSurvey">
              <span v-if="!isSubmitting">Finish</span>
              <span v-else class="spinner"></span>
            </button>
          </div>
        </div>
        <div v-else-if="surveyCompleted">
          <div class="questionnaire-header">
            <h2>Your opinion is appreciated and will be reviewed by our staff shortly.</h2>
          </div>
          <div class="rewards-container">
            <div v-if="hasReward">
              <v-icon class="gift-icon" size="30px" :icon="mdiGift"></v-icon>
              <h1 class="gift-name">{{ rewardName }}</h1>
              <h2 class="gift-description">{{ rewardDescription }}</h2>
            </div>
            <div v-if="instantReward" class="reward-link">
              Your link is: <a :href="rewardLink" target="_blank">{{ rewardLink }}</a>
            </div>
            <div v-else>
              <p class="rewards-body">A confirmation email will be sent to {{ userEmail }}</p>
            </div>
          </div>
          <div v-if="hasMlf && hasOrders">
            <div class="questionnaire-header">
              <h2>We would love to hear your thoughts on the items you ordered.</h2>
              <div v-for="order in mlfItems" :key="order.itemId" class="snapshot-reasons">
                <p class="snapshot-label">{{ order.itemLabel }}</p>
                <WRatingsBar
                  section-tag="ratings-widget-mlf"
                  v-bind="{ useFaces: false, useColors: false, useBorder: false, size: '40px' }"
                  v-model="mlfRatingBars[order.itemId]"
                  @update:model-value="(newRating) => updateMlfItem(order.itemId, { rating: newRating })"
                />
              </div>
            </div>
          </div>

          <div v-if="!hasMlf || !hasOrders" class="questionnaire-footer">
            <button class="snapshot-next" @click="toggleVisibility">Exit</button>
          </div>
          <div v-if="hasMlf && hasOrders">
            <button class="snapshot-return" @click="toggleVisibility">Exit</button>
            <button class="snapshot-next" @click="submitMlf">Finish</button>
          </div>
        </div>
        <div v-if="isFinalized">
          <h1>Thank you for taking this survey!</h1>
          <p>You may now close this widget.</p>
        </div>
      </div>
    </div>
  </body>
</template>

<script setup lang="ts">
import WRatingsBar from '@/components/wasabi/dataVisualization/WRatingsBar.vue'
import { ref, reactive } from 'vue'
import { useMerchant } from '../stores/Merchant'
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { mdiEmoticonSadOutline, mdiEmoticonNeutralOutline, mdiEmoticonHappyOutline, mdiGift } from '@mdi/js'
import axios from 'axios'

const store = useMerchant()
let rating = defineModel()
let isVisible = ref(true)
let isFinalized = ref(false)
let isExpanded = ref(false)
let hasOrders = ref(false)
let choosingLocation = ref(false)
let answeringQuestionnaire = ref(false)
let answeringValidation = ref(false)
let answeringAdditionalQuestions = ref(false)
let surveyCompleted = ref(false)
let answeringSnapshots = ref(false)
let snapshotTitle = ref(false)
let isMinimized = ref(false)
let hasReward = ref(false)
let hasMlf = ref(false)
let hasValidation = ref(false)
let rewardName = ''
let rewardDescription = ''
let ratingsBarVisible = ref(true)
let filterText = ref('')
let storedEmail = ref('')
let selectedLocation = ref(null)
let selectedQuestionnaire = ref(null)
let questionnaireChoice = ref(null)
let surveyStartTime = ref<Date | null>(null)
let surveyEndTime = ref<Date | null>(null)
let selectedDate = ref(new Date())
let selectedTime = ref(new Date())
let previousTime = ref(new Date())
let timerRunning = ref(false)
let userEmail = ref('')
let resendCode = ref('Resend code')
let snapshotComment = ref('')
const snapshotComments = ref({})
let snapshotErrorMessage = ref('')
let hasError = ref(true)
let snapshotError = ref(true)
let verificationError = ref(true)
let verificationSeconds = ref(0)
let orderTransactionId = ref(0)
let remainingAttempts = ref(6)
let questionnaireErrors = ref('')
let rewardResponse = ref({})
let verificationCode = ref(null)
let instantReward = ref(false)
const { t } = useI18n()
let currentTitle = ref('dine_title')
const currentSnapshotIndex = ref(0)
const ratingValidationMessage = ref('')
const isCommentRequired = ref(false)
const validationMessages = ref({})
const isSubmitting = ref(false)
const isLoadingLocations = ref(false)
const feedbackAlreadyReceived = ref(true)

let mlfItems = ref([])

const surveyData = ref({
  dateTimeExperiencedLocal: new Date().toISOString(),
  locationsId: 0,
  questionnaireId: 0,
  rating: 0,
  subscribe: true,
  employeeId: 0,
  tattleLatitude: 0,
  tattleLongitude: 0,
  tracker: '',
  sessionId: 'string',
  originalLocationsId: 0,
  snapshots: [],
  answers: [],
  // checkNumber: 'string',
  email: 'string',
  // ref: 'string',
  // partnersId: 0, The partnersId set to zero causes API call to fail
  surveyTimeInSeconds: 0,
  viewportWidth: window.innerWidth,
  viewportHeight: window.innerHeight,
  // captchaToken: 'string',
})

const mlfData = ref({
  merchantId: 0,
  locationId: 0,
  transactionId: 0,
  items: [],
})

let snapshotRatingBars = ref({})
let mlfRatingBars = ref({})

const rewardLink = computed(() => {
  if (rewardResponse.value && rewardResponse.value.customersReward) {
    const baseUrl = 'https://customer-web-app.dev.tattle-internal.net/redeem/'
    const customersRewardId = rewardResponse.value.customersReward.id
    const redeemCode = rewardResponse.value.customersReward.code

    return `${baseUrl}${customersRewardId}?redeemCode=${redeemCode}`
  }
  return null
})

const widgetTitle = computed(() => t(`message.${currentTitle.value}`))

const hasAnyImpactRating = computed(() => {
  const currentSnapshotReasons =
    surveyData.value.snapshots.find((s) => s.snapshotId === currentSnapshot.value?.snapshotId)?.reasons || []

  return currentSnapshotReasons.some(
    (reason) => snapshotRatingBars.value[reason.reasonsId] === -1 || snapshotRatingBars.value[reason.reasonsId] === 1,
  )
})

const validateComment = () => {
  const currentRating = snapshotRatingBars.value[currentSnapshot.value.id]
  const currentComment = snapshotComments.value[currentSnapshot.value.snapshotId]

  if (currentRating <= 2 && currentComment && currentComment.trim() !== '') {
    validationMessages.value[currentSnapshot.value.snapshotId] = ''
  }
}

const handleRatingChange = (newRating, snapshotId = null) => {
  const targetSnapshotId = snapshotId || currentSnapshot.value.snapshotId

  validationMessages.value[targetSnapshotId] = ''

  if (newRating <= 2) {
    validationMessages.value[targetSnapshotId] = `Please explain why you gave a ${newRating} star rating`
    isCommentRequired.value = true
  } else if (newRating === 3 && !hasAnyImpactRating.value) {
    validationMessages.value[targetSnapshotId] = 'Please select at least one factor that impacted your rating'
    isCommentRequired.value = false
  } else {
    isCommentRequired.value = false
  }

  updateSnapshot(targetSnapshotId, { rating: newRating })
}

const getLocations = computed(() => {
  const filter = filterText.value.toLowerCase()
  return store.getMerchantLocations.filter((location) => {
    const address =
      `${location.label} ${location.address.addressLineOne} ${location.address.addressLineTwo}`.toLowerCase()
    return address.includes(filter)
  })
})

const setSurveyDuration = () => {
  if (surveyStartTime.value && surveyEndTime.value) {
    const diff = surveyEndTime.value.getTime() - surveyStartTime.value.getTime()
    const durationInSeconds = Math.floor(diff / 1000)
    surveyData.value.surveyTimeInSeconds = durationInSeconds
  } else {
    console.warn('Unable to set survey duration: start or end time is missing')
  }
}

const startSurveyTimer = () => {
  if (!timerRunning.value) {
    surveyStartTime.value = new Date()
    timerRunning.value = true
  }
}

const submitSurvey = async () => {
  isSubmitting.value = true
  surveyEndTime.value = new Date()
  setSurveyDuration()
  const answers = getSnapshots.value.questions.map((question) => {
    let answer = {
      questionId: question.id,
      answerId: 0 | null,
      weightless: question.weightless || false,
      weight: question.weight || 0,
      answerText: '',
    }

    if (question.fieldType === 'radio') {
      const selectedRadio = document.querySelector(`input[name="${question.id}"]:checked`)
      if (selectedRadio) {
        answer.answerId = parseInt(selectedRadio.id)
        answer.weight = parseFloat(selectedRadio.value)
        answer.answerText = selectedRadio.dataset.answerTitle
      }
    } else if (question.fieldType === 'textarea') {
      const textarea = document.querySelector(`textarea[name="${question.id}"]`)
      if (textarea) {
        answer.answerId = null
        answer.answerText = textarea.value.trim()
      }
    }
    return answer
  })
  surveyData.value.answers = answers
  try {
    const response = await axios.post(
      'https://api.dev.tattleapp.io/surveys/api/customer-survey/widget/submit',
      surveyData.value,
    )
    console.log('Survey submitted successfully:', response.data)
    rewardResponse.value = response.data
    answeringAdditionalQuestions.value = false
    surveyCompleted.value = true
    currentTitle.value = 'We Appreciate Your Feedback!'
  } catch (error) {
    feedbackAlreadyReceived.value = false
    console.error('Error submitting survey:', error)
  } finally {
    isSubmitting.value = false
  }
  orderLookup()
}

const submitMlf = async () => {
  try {
    const response = await axios.post(
      'https://api.dev.tattleapp.io/surveys/widget/menu-level-feedback/submit',
      mlfData.value,
    )
    console.log('MLF data submitted successfully:', response.data)
  } catch (error) {
    console.error('Error submitting MLF Data:', error)
  }
  surveyCompleted.value = !surveyCompleted.value
  isFinalized.value = !isFinalized.value
  toggleExpanded()
}

function updateField(field, value) {
  if (field in surveyData.value) {
    surveyData.value[field] = value
  } else {
    console.warn(`Field "${field}" does not exist in surveyData`)
  }
}

function addMlfItem(productMappingId, itemId, orderItemId, itemLabel) {
  const mlfItem = {
    productMappingId: productMappingId,
    itemId: itemId,
    orderItemId: orderItemId,
    itemLabel: itemLabel,
    rating: 0,
  }
  mlfData.value.items.push(mlfItem)
}

function addSnapshot(snapshot) {
  surveyData.value.snapshots.push(snapshot)
}

function updateSnapshot(snapshotId, updatedFields) {
  const index = surveyData.value.snapshots.findIndex((s) => s.snapshotId === snapshotId)
  if (index !== -1) {
    surveyData.value.snapshots[index] = { ...surveyData.value.snapshots[index], ...updatedFields }
  } else {
    console.warn(`Snapshot with ID ${snapshotId} not found`)
  }
}

function updateMlfItem(itemId, updatedFields) {
  const itemIndex = mlfData.value.items.findIndex((item) => item.itemId === itemId)
  if (itemIndex !== -1) {
    mlfData.value.items[itemIndex] = {
      ...mlfData.value.items[itemIndex],
      ...updatedFields,
    }
  } else {
    console.warn(`MLF item with ID ${itemId} not found`)
  }
}
function updateReason(snapshotId, reasonId, updatedFields) {
  const snapshotIndex = surveyData.value.snapshots.findIndex((s) => s.snapshotId === snapshotId)
  if (snapshotIndex !== -1) {
    const snapshot = surveyData.value.snapshots[snapshotIndex]
    const reasonIndex = snapshot.reasons.findIndex((r) => r.reasonsId === reasonId)
    if (reasonIndex !== -1) {
      surveyData.value.snapshots[snapshotIndex].reasons[reasonIndex] = {
        ...surveyData.value.snapshots[snapshotIndex].reasons[reasonIndex],
        ...updatedFields,
      }
    } else {
      console.warn(`Reason with ID ${reasonId} not found in snapshot ${snapshotId}`)
    }
  } else {
    console.warn(`Snapshot with ID ${snapshotId} not found`)
  }
}

const getQuestions = computed(() => {
  return store.getMerchantQuestions
})

const getSnapshots = computed(() => {
  return store.getMerchantSnapshots
})

const currentSnapshot = computed(() => {
  return getSnapshots.value.questionnaireSnapshots[currentSnapshotIndex.value]
})

const isLastSnapshot = computed(() => {
  return currentSnapshotIndex.value === getSnapshots.value.questionnaireSnapshots.length - 1
})

const submitSnapshot = (snapshotId, rating) => {
  const comment = snapshotComments.value[snapshotId] || ''

  if (rating === -1) {
    handleSnapshotSubmission('User skipped this snapshot.', snapshotId, rating)
    return
  }

  if (rating === null || rating === undefined) {
    validationMessages.value[snapshotId] = 'Please select a star rating'
    snapshotError.value = false
    return
  }

  if (rating <= 2 && (!comment || comment.trim() === '')) {
    validationMessages.value[snapshotId] = `Please explain why you gave a ${rating} star rating`
    snapshotError.value = false
    return
  }

  if (rating === 3 && !hasAnyImpactRating.value) {
    validationMessages.value[snapshotId] = 'Please select at least one factor that impacted your rating'
    snapshotError.value = false
    return
  }

  handleSnapshotSubmission(comment, snapshotId, rating)
}

const handleSnapshotSubmission = (comment, snapshotId, rating) => {
  updateSnapshot(snapshotId, { rating, comment })
  snapshotComment.value = ''
  snapshotError.value = true
  validationMessages.value[snapshotId] = ''
  if (!isLastSnapshot.value) {
    currentSnapshotIndex.value++
    currentTitle.value = getSnapshots.value.questionnaireSnapshots[currentSnapshotIndex.value].snapshot.label
  } else {
    handleAdditionalQuestions()
  }
}

const previousSnapshot = () => {
  if (currentSnapshotIndex.value === 0) {
    answeringSnapshots.value = false
    answeringAdditionalQuestions.value = false
    currentTitle.value = 'Can we ask additional questions?'
    toggleQuestionnaire()
  } else if (currentSnapshotIndex.value === getSnapshots.value.questionnaireSnapshots.length - 1) {
    answeringSnapshots.value = !answeringSnapshots.value
    answeringAdditionalQuestions.value = !answeringAdditionalQuestions.value
    currentSnapshotIndex.value--
    currentTitle.value = getSnapshots.value.questionnaireSnapshots[currentSnapshotIndex.value].snapshot.label
    validateCurrentSnapshotRating()
  } else {
    currentSnapshotIndex.value--
    currentTitle.value = getSnapshots.value.questionnaireSnapshots[currentSnapshotIndex.value].snapshot.label
    validateCurrentSnapshotRating()
  }
}

const validateCurrentSnapshotRating = () => {
  const currentRating = snapshotRatingBars.value[currentSnapshot.value.id]

  ratingValidationMessage.value = ''
  isCommentRequired.value = false

  if (currentRating === null || currentRating === undefined) {
    ratingValidationMessage.value = 'Please select a star rating'
    return
  }
}

const submitVerification = async () => {
  try {
    const response = await axios.post(
      'https://api.dev.tattleapp.io/surveys/api/customer-survey/widget/verify-email-code',
      {
        merchantId: store.getMerchantId,
        locationId: surveyData.value.locationsId,
        questionnaireId: surveyData.value.questionnaireId,
        surveyTrackerId: surveyData.value.sessionId,
        verificationCode: verificationCode.value,
      },
    )

    if (response.data.success) {
      console.log('Email verified:', response.data)
      answeringValidation.value = !answeringValidation.value
      toggleSnapshots()
    } else {
      console.log('Email failed:', response.data)
      verificationError.value = !verificationError.value
      handleVerificationError(response.data.secondsUntilNextAttempt)
    }
  } catch (error) {
    console.error('Error verifying email:', error)
  }
}

const resendVerification = async () => {
  resendCode.value = 'Sending code...'
  try {
    const response = await axios.post(
      'https://api.dev.tattleapp.io/surveys/api/customer-survey/widget/send-verification',
      {
        sessionId: surveyData.value.sessionId,
        emailAddress: surveyData.value.email,
        merchantId: store.getMerchantId,
      },
    )
    console.log('Verification email sent:', response.data)
    resendCode.value = 'Code sent'
  } catch (error) {
    resendCode.value = 'Resend code'
    console.error('Error sending email:', error)
  }
}

const orderLookup = async () => {
  const apiId = store.getMerchantId
  const today = new Date()
  const twoWeeksAgo = new Date(today)
  twoWeeksAgo.setDate(today.getDate() - 14)

  const year = today.getFullYear()
  const month = today.getMonth() + 1
  const day = today.getDate()

  const twoWeeksAgoYear = twoWeeksAgo.getFullYear()
  const twoWeeksAgoMonth = twoWeeksAgo.getMonth() + 1
  const twoWeeksAgoDay = twoWeeksAgo.getDate()

  const formattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
  const formattedTwoWeeksAgo = `${twoWeeksAgoYear}-${twoWeeksAgoMonth.toString().padStart(2, '0')}-${twoWeeksAgoDay.toString().padStart(2, '0')}`

  mlfData.value.merchantId = store.getMerchantId
  mlfData.value.locationId = surveyData.value.locationsId

  try {
    const response = await axios.post(`https://api.dev.tattleapp.io/surveys/api/order-lookup/widget/${apiId}/contact`, {
      merchantId: store.getMerchantId,
      StartDate: formattedTwoWeeksAgo,
      EndDate: formattedDate,
      Email: surveyData.value.email,
    })
    console.log('Order lookup:', response.data)
    mlfItems.value = response.data
  } catch (error) {
    console.error('Error sending lookup:', error)
  }
  if (mlfItems.value.length !== 0) {
    mlfItems.value.forEach((order) => {
      addMlfItem(order.productMappingId, order.itemId, order.orderItemId, order.itemLabel)
    })
    extractOrderId(mlfData.value.items[0].orderItemId)
  }
}

const handleAdditionalQuestions = () => {
  currentTitle.value = 'Additional Questions'
  answeringSnapshots.value = !answeringSnapshots.value
  answeringAdditionalQuestions.value = !answeringAdditionalQuestions.value
}

function handleLocation(location) {
  updateField('locationsId', location.id)
  selectedLocation.value = location
  store.fetchAndStoreQuestionnaireData(location.id)
  currentTitle.value = 'Can we ask a few more questions?'
  toggleChoosingLocation()
  toggleSnapshotTitle()
  toggleQuestionnaire()
  startSurveyTimer()
  selectedTime.value.setMinutes(0)
  previousTime.value.setMinutes(0)
  previousTime.value.setHours(selectedTime.value.getHours() - 1)
}

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    hour12: true,
  })
}

const changeTime = (direction: number) => {
  const newTime = new Date(selectedTime.value)
  const oldTime = new Date(previousTime.value)
  newTime.setHours(newTime.getHours() + direction)
  newTime.setMinutes(0)
  oldTime.setHours(oldTime.getHours() + direction)
  oldTime.setMinutes(0)

  const now = new Date()

  if (newTime <= now) {
    selectedTime.value = newTime
    previousTime.value = oldTime
  }
}

const isCurrentHour = (date: Date): boolean => {
  const now = new Date()
  return (
    date.getHours() === now.getHours() &&
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  )
}

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    weekday: 'short',
    day: 'numeric',
  })
}

const changeDate = (direction: number) => {
  const currentDate = selectedDate.value
  const newDate = new Date(currentDate)
  newDate.setDate(newDate.getDate() + direction)

  if (newDate <= new Date()) {
    selectedDate.value = newDate
  }
}

const isCurrentDate = (date: Date): boolean => {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

function validateEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

function extractOrderId(orderItemId) {
  hasOrders.value = !hasOrders.value
  const orderIdPart = orderItemId.split('-')[0]
  orderTransactionId.value = orderIdPart
  mlfData.value.transactionId = orderTransactionId.value
}

function handleVerificationError(seconds) {
  verificationSeconds.value = seconds
  remainingAttempts.value -= 1

  if (remainingAttempts.value > 0) {
    const timer = setInterval(function () {
      verificationSeconds.value -= 1
      if (verificationSeconds.value === 0) {
        if (remainingAttempts.value != 0) {
          clearInterval(timer)
          verificationError.value = !verificationError.value
        } else {
          console.log('Zero attempts')
        }
      }
    }, 1000)
  }
}

function handleQuestionnaire(question) {
  selectedQuestionnaire.value = question
  store.fetchAndStoreSnapshotData(selectedLocation.value.id, selectedQuestionnaire.value.id)
  hasError.value = true
  if (question && question.reward) {
    if (question.rewardAssigned.waitTime === 0) {
      instantReward.value = true
    }
    hasReward.value = true
    rewardName = question.reward.label
    rewardDescription = question.reward.description
  } else {
    hasReward.value = false
    console.log('No associated reward.')
  }

  if (question && question.includeMlf) {
    hasMlf.value = true
  }

  if (question && question.verifyEmail) {
    hasValidation.value = true
  } else {
    hasValidation.value = false
  }
}

const submitQuestionnaire = async () => {
  let caughtErrors = ''
  if (selectedQuestionnaire.value === null) {
    hasError.value = false
    caughtErrors = 'Please select a questionnaire'
    questionnaireErrors.value = caughtErrors
  } else {
    if (!validateEmail(userEmail.value) && selectedQuestionnaire.value.requiresEmail === 'required') {
      hasError.value = false
      caughtErrors = 'Email address required'
      questionnaireErrors.value = caughtErrors
    } else if (
      userEmail.value !== '' &&
      !validateEmail(userEmail.value) &&
      selectedQuestionnaire.value.requiresEmail === 'optional'
    ) {
      hasError.value = false
      caughtErrors = 'Email address is not valid'
      questionnaireErrors.value = caughtErrors
    } else {
      updateField('questionnaireId', selectedQuestionnaire.value.id)
      updateField('email', userEmail.value)
      updateField(
        'dateTimeExperiencedLocal',
        new Date(
          selectedDate.value.setHours(selectedTime.value.getHours(), selectedTime.value.getMinutes()),
        ).toISOString(),
      )

      if (validateEmail(userEmail.value)) {
        try {
          const response = await axios.post(
            'https://api.dev.tattleapp.io/surveys/api/customer-survey/widget/start-session',
            {
              merchantId: store.getMerchantId,
              locationId: surveyData.value.locationsId,
              questionnaireId: surveyData.value.questionnaireId,
              emailAddress: surveyData.value.email,
            },
          )
          if (userEmail.value !== storedEmail.value) {
            console.log('Session successfully started:', response.data)
            surveyData.value.sessionId = response.data.sessionId
          }
        } catch (error) {
          console.error('Error starting session:', error)
        }
      }

      surveyData.value.snapshots = []

      getSnapshots.value.questionnaireSnapshots.forEach((snapshot) => {
        snapshot.reasons.forEach((reason) => {
          snapshotRatingBars.value[reason.id] = 0
        })
        addSnapshot({
          snapshotId: snapshot.snapshotId,
          rating: 0,
          comment: '',
          reasons: snapshot.reasons.map((reason) => ({
            reasonsId: reason.reasonId,
            impact: 0,
            menuItems: reason.reason.label,
          })),
        })

        snapshotRatingBars.value[snapshot.snapshotId] = null
        snapshotRatingBars.value[`${snapshot.snapshot.label} comment`] = ''
        snapshot.reasons.forEach((reason) => {
          snapshotRatingBars.value[reason.reasonId] = 0
        })
      })

      if (hasValidation.value) {
        toggleValidation(userEmail.value)
      } else {
        currentTitle.value = currentSnapshot.value.snapshot.label
        toggleQuestionnaire()
        return toggleSnapshots()
      }
    }
  }
}

function setSnapshotRating(reasonId, rating, snapshot) {
  if (snapshot === null) {
    snapshotError.value = false
    updateReason(snapshot, reasonId, { impact: rating })
    snapshotRatingBars.value[reasonId] = rating
  } else if (snapshotRatingBars.value[reasonId] !== rating) {
    snapshotError.value = true
    updateReason(snapshot, reasonId, { impact: rating })
    snapshotRatingBars.value[reasonId] = rating

    const currentRating = snapshotRatingBars.value[currentSnapshot.value.id]
    if (currentRating === 3) {
      if (hasAnyImpactRating.value) {
        validationMessages.value[currentSnapshot.value.snapshotId] = ''
      } else {
        validationMessages.value[currentSnapshot.value.snapshotId] =
          'Please select at least one factor that impacted your rating'
      }
    }
  }
}

const props = defineProps<{
  slug: string
}>()

onMounted(() => {
  store.fetchAndStoreBootstrapData(props.slug)
})

async function handleOverallRating(rating) {
  isLoadingLocations.value = true
  currentTitle.value = `questionnaire_location`
  updateField('rating', rating)

  try {
    await store.fetchAndStoreBootstrapData(props.slug)
  } catch (error) {
    console.error('Error loading locations:', error)
  } finally {
    isLoadingLocations.value = false
    toggleRatingsBar()
    toggleChoosingLocation()
    toggleExpanded()
  }
}

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
}

function toggleChoosingLocation() {
  choosingLocation.value = !choosingLocation.value
}

function toggleChangeLocation() {
  currentTitle.value = 'questionnaire_location'
  toggleSnapshotTitle()
  toggleChoosingLocation()
  toggleQuestionnaire()
}

function toggleQuestionnaire() {
  answeringQuestionnaire.value = !answeringQuestionnaire.value
}

function cancelValidation() {
  currentTitle.value = 'Can we ask additional questions?'
  answeringQuestionnaire.value = !answeringQuestionnaire.value
  answeringValidation.value = !answeringValidation.value
}

const toggleValidation = async (email) => {
  currentTitle.value = 'Verify Your Email Address'
  answeringQuestionnaire.value = !answeringQuestionnaire.value
  answeringValidation.value = !answeringValidation.value

  if (email !== storedEmail.value) {
    remainingAttempts.value = 6
    storedEmail.value = email
    try {
      const response = await axios.post(
        'https://api.dev.tattleapp.io/surveys/api/customer-survey/widget/send-verification',
        {
          sessionId: surveyData.value.sessionId,
          emailAddress: surveyData.value.email,
          merchantId: store.getMerchantId,
        },
      )
      console.log('Verification email sent:', response.data)
    } catch (error) {
      console.error('Error sending email:', error)
    }
  } else if (storedEmail.value === email) {
    console.log("Email didn't change")
  }
}

function toggleSnapshots() {
  answeringSnapshots.value = !answeringSnapshots.value
}

function toggleMinimized() {
  isMinimized.value = !isMinimized.value
}

function toggleVisibility() {
  isVisible.value = !isVisible.value
}

function toggleSnapshotTitle() {
  snapshotTitle.value = !snapshotTitle.value
}

function toggleRatingsBar() {
  ratingsBarVisible.value = !ratingsBarVisible.value
}
</script>

<style scoped>
.widget {
  box-sizing: content-box;
  position: fixed;
  bottom: 0px;
  right: 0px;
  width: 320px;
  height: 176px;
  transition: height 0.3s ease;
  background-color: #cc3f26;
  box-shadow: black 10px 10px;
}

header {
  box-sizing: border-box;
  display: block;
  align-items: center;
  cursor: pointer;
  width: 100%;
  transition: 175ms;
  max-width: 320px;
}

h1 {
  font-family: 'Roboto', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: black;
  box-sizing: border-box;
  display: block;
  text-align: center;
  max-width: 295px;
  padding: 16px 0px 16px 0px;
  margin: 0px auto;
  white-space: pre-wrap;
  position: static;
}

h2 {
  font-size: 14px;
  margin: 10px 20px;
}

.face-rating {
  display: flex;
  justify-content: space-around;
  flex-direction: row;
  padding-top: 10px;
}

.positive-impact {
  color: #979797;
  fill: none;
  cursor: pointer;
  background: transparent;
  transition: 150ms;
}

.positive-impact:hover {
  fill: green;
  color: green;
  stroke: green;
  transform: scale(1.1);
  transition: 150ms;
}

.positiveselected {
  fill: green;
  color: green;
  stroke: #008000;

  border: 1px solid rgba(0, 128, 0, 0.3);
  border-radius: 50%;
  background-color: rgba(0, 128, 0, 0.1);
}

.neutral-impact {
  color: #979797;
  fill: none;
  cursor: pointer;
  background: transparent;
  transition: 150ms;
}

.neutral-impact:hover {
  fill: grey;
  color: grey;
  stroke: grey;
  transform: scale(1.1);
  transition: 150ms;
}

.neutralselected {
  fill: grey;
  color: grey;
  stroke: grey;

  border: 1px solid rgba(128, 128, 128, 0.3);
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.1);
}

.negative-impact {
  color: #979797;
  fill: none;
  cursor: pointer;
  background: transparent;
  transition: 150ms;
}

.negativeselected {
  fill: red;
  color: red;
  stroke: red;

  border: 1px solid rgba(255, 0, 0, 0.3);
  border-radius: 50%;
  background-color: rgba(255, 0, 0, 0.1);
}

.negative-impact:hover {
  fill: red;
  color: red;
  stroke: red;
  transform: scale(1.1);
  transition: 150ms;
}

.filter {
  display: block;
  width: 100%;
  box-sizing: border-box;
  border-width: medium medium 1px;
  border-style: none none solid;
  border-color: currentcolor currentcolor rgb(118, 118, 118);
  border-image: none;
  padding: 8px;
  margin: 6px auto;
  background: rgb(250, 251, 252);
  font-size: 14px;
}

.promo-containter {
  cursor: pointer;
  box-sizing: border-box;
  text-align: left;
}

.selection {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 14px 0px 14px 30px;
  border-bottom: 1px solid rgba(120, 138, 175, 0.13);
  background: rgb(255, 255, 255);
}

.reward-label {
  background-color: rgba(120, 138, 175, 0.1);
  border-radius: 12px;
  padding: 4px 16px;
  color: #495a7c;
  margin-top: 10px;
  margin-left: 20px;
  width: fit-content;
  font-size: 12px;
}

.snapshot-reasons {
  padding: 21px 14px 32px 14px;
  border-bottom: 1px solid rgba(120, 138, 175, 0.13);
  background: rgb(255, 255, 255);
}

.snapshot-label {
  font-size: 16px;
  font-weight: 700;
  margin: 0px;
  padding: 14px 0px 8px;
  color: rgb(68, 68, 68);
}

.search {
  padding: 0.25rem 1rem;
  background: rgb(250, 251, 252);
}

.question-button {
  margin: 0px;
  cursor: pointer;
}

.submit {
  font-size: 16px;
  border: medium;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  color: white;
  font-weight: bold;
  background-color: #cc3f26;
  margin: 1rem 0.25rem 0.25rem;
  min-width: 121px;
  min-height: 38px;
  cursor: pointer;
  outline: none;
  height: fit-content;
}

.submit.disabled {
  background-color: dimgrey;
  opacity: 0.5;
}

.change-location {
  line-height: 1.4em;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  background: none;
  border: medium;
  text-decoration: underline;
  color: #cc3f26;
  font-weight: bold;
  cursor: pointer;
}

.questionnaire-list {
  flex-direction: column;
  display: flex;
  justify-content: center;
}

.questionnaire-skip {
  text-decoration: underline;
  color: #cc3f26;
  font-weight: bold;
  cursor: pointer;
  width: 100%;
  padding: 16px 2px;
}

.questionnaire-skippable {
  text-decoration: underline;
  color: #cc3f26;
  font-weight: bold;
  cursor: pointer;
  width: 100%;
  padding: 16px 2px;
}

.questionnaire-unskippable {
  opacity: 0;
  font-weight: bold;
  cursor: disabled;
  width: 100%;
  padding: 16px 2px;
}

.questionnaire-error {
  color: crimson;
  width: 100%;
  font-size: 14px;
  text-align: center;
  line-height: 14px;
  margin-top: 8px;
}

.snapshot-error {
  padding: 3px;
  color: crimson;
  width: 100%;
  font-size: 16px;
  text-align: center;
  line-height: 14px;
  margin-bottom: 10px;
}

.snapshot-header {
  margin: 0px auto;
  padding: 16px 0.5rem;
}

.wrapper {
  min-height: 9rem;
  background: rgb(250, 251, 252);
  border-top: 1px solid rgba(33, 81, 101, 0.08);
  padding-bottom: 60px;
}

.cancel-button {
  background: rgb(255, 255, 255);
  border: 1px solid red;
  border-radius: 7px;
  padding: 7px 14px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.43;
  margin: 0px auto;
  cursor: pointer;
}

.email-field {
  display: block;
  width: calc(100% - 2rem);
  box-sizing: border-box;
  border-width: medium medium 1px;
  border-style: none none solid;
  border-color: currentColor currentColor rgb(118, 118, 118);
  border-image: none;
  padding: 8px;
  margin: 1rem auto;
  background: rgb(255, 255, 255);
  caret-color: #cc3f26;
  font-size: 14px;
  outline-color: #cc3f26;
}

.arrow-button {
  background: rgb(255, 255, 255);
  border: medium;
  border-radius: 3px;
  box-shadow: rgba(21, 37, 43, 0.1) 0px 2px 8px 0px;
  color: #cc3f26;
  cursor: pointer;
}

.arrow-button:disabled {
  opacity: 0.2;
  background: linear-gradient (to right, transparent, rgb(202, 100, 120));
  cursor: not-allowed;
}

.question-label {
  display: inline-block;
  padding: 0.5rem;
  font-size: 14px;
}

.picker {
  border-left: 1px solid rgba(120, 138, 175, 0.25);
  border-right: 1px solid rgba(120, 138, 175, 0.25);
  line-height: 1.4em;
  padding: 10px;
  flex: 1 1 0%;
  text-align: center;
  cursor: pointer;
}

.pickers {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  padding: 0.5rem 1rem;
  background: rgb(255, 255, 255);
}

.date-picker {
  display: flex;
  margin: 10px;
  border-radius: 3px;
  border: 1px solid rgba(120, 138, 175, 0.25);
  width: 90%;
}

.time-picker {
  display: flex;
  margin: 10px;
  border-radius: 3px;
  border: 1px solid rgba(120, 138, 175, 0.25);
  width: 90%;
}

.location-title {
  font-size: 15px;
}

.questionnaire-header {
  cursor: auto;
  border-top: 1px solid rgba(33, 81, 101, 0.08);
  border-bottom: 1px solid rgba(33, 81, 101, 0.08);
  color: rgb(71, 71, 71);
  margin: 0px;
}

.questionnaire-footer {
  cursor: auto;
  min-height: 9rem;
  padding-bottom: 60px;
  border-top: 1px solid rgba(33, 81, 101, 0.08);
  color: rgb(71, 71, 71);
  margin: 0px;
}

.resend-link {
  cursor: pointer;
  text-decoration: underline;
}

.spinner {
  display: inline-block;
  margin-top: 2px;
  width: 12px;
  height: 12px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
}

.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
  margin-top: -15px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(204, 63, 38, 0.2);
  border-radius: 50%;
  border-top-color: #cc3f26;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.snapshot-return {
  background-color: white;
  border: 1px solid #cc3f26;
  white-space: nowrap;
  display: inline;
  color: rgb(71, 71, 71);
  font-size: 16px;
  padding: 0.5rem 1rem;
  font-weight: bold;
  margin: 1rem 0.25rem 0.25rem;
  min-width: 87px;
  min-height: 20px;
  cursor: pointer;
  outline: none;
}

.snapshot-next {
  background-color: #cc3f26;
  border: 1px solid #cc3f26;
  white-space: nowrap;
  display: inline;
  color: white;
  font-size: 16px;
  padding: 0.5rem 1rem;
  font-weight: bold;
  margin: 1rem 0.25rem 0.25rem;
  min-width: 87px;
  min-height: 20px;
  cursor: pointer;
  outline: none;
}

.snapshot-next:disabled {
  background-color: grey;
  border: 1px solid grey;
  white-space: nowrap;
  display: inline;
  color: white;
  font-size: 16px;
  padding: 0.5rem 1rem;
  font-weight: bold;
  margin: 1rem 0.25rem 0.25rem;
  min-width: 87px;
  min-height: 20px;
  cursor: default;
  outline: none;
}

.questionnaire-location {
  display: flex;
  box-sizing: border-box;
  text-align: left;
  align-items: center;
}

.questionnaire-snapshot {
  font-size: 16px;
  background: rgb(250, 251, 252);
}

.validation {
  font-size: 16px;
  background: rgb(250, 251, 252);
  padding-bottom: 30px;
}

.gift-icon {
  margin: 20px 10px 0 10px;
}

.gift-name {
  margin: 0px 10px 0 10px;
}

.gift-description {
  margin: 0px 10px 10px 10px;
}

.rewards-body {
  margin: 0px 10px 0px 10px;
  padding-bottom: 20px;
}

.rewards-container {
  margin: 0;
  line-height: 1.42857;
  padding-left: 0;
  padding-right: 0;
  box-sizing: border-box;
  font-size: 14px;
  background: rgb(250, 251, 252);
}

.reward-link {
  padding-bottom: 20px;
}

.additional-questions {
  line-height: 1.42857;
  margin: 0;
  padding-left: 0;
  padding-right: 0;
  box-sizing: border-box;
  font-size: 14px;
  background: rgb(250, 251, 252);
}

.additional-list {
  position: relative;
  display: block;
  box-sizing: border-box;
}

.additional-label {
  text-align: left;
  width: 100%;
  padding: 16px 0 16px 80px;
  border-bottom: solid 1px #f4f5f7;
  min-height: 20px;
  margin-bottom: 0;
  font-weight: normal;
  cursor: pointer;
  display: inline-block;
  max-width: 100%;
  position: relative;
  box-sizing: border-box;
}

.additional-radio {
  position: absolute;
  margin-top: 3px;
  margin-left: -20px;
}

.snapshot-text {
  width: calc(100% - 2rem);
  min-width: calc(100% - 2rem);
  max-width: calc(100% - 2rem);
  min-height: 100px;
  max-height: 100px;
  box-sizing: border-box;
  border: 1px solid rgba(120, 138, 175, 0.23);

  border-radius: 2px;
  padding: 6px;
  background: rgb(255, 255, 255);
  margin: 1rem;
  caret-color: #cc3f26;
  outline-color: #cc3f26cc;
}

.closeButton {
  display: flex;
  font-family: 'Roboto', sans-serif;
  font-weight: 800;
  font-size: 20px;
  position: absolute;
  padding: 0px 0.5rem;
  top: 5px;
  right: 0px;
  border: none;
  background-color: transparent;
  opacity: 0.7;
  color: #bcbcbc;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 0px;
}

.helper {
  max-width: 420px;
  margin: auto;
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  display: flex;
  position: absolute;
  background-color: red;
}

.location-button {
  list-style: none;
  padding: 1rem;
  display: flex;
  position: relative;
  background: rgb(255, 255, 255);
  align-items: flex-end;
  justify-content: space-between;
  line-height: 1.4em;
  color: rgb(83, 83, 83);
  cursor: pointer;
  font-size: 13px;
  box-shadow:
    rgba(33, 81, 101, 0.08) 0px 1px 2px 0px,
    rgba(33, 81, 101, 0.11) 0px 0px 6px 0px;
  text-align: left;
}

.controls {
  box-shadow:
    rgba(33, 81, 101, 0.08) 0px 0px 4px 0px,
    rgba(33, 81, 101, 0.08) 0px 1px 2px 0px;
  background-color: rgb(250, 251, 252);
  display: flex;
  justify-content: flex-end;
  padding: 15px;
  margin-bottom: 3rem;
}

.cancel {
  display: flex;
  background: rgb(255, 255, 255);
  border: 1px solid red;
  border-radius: 7px;
  padding: 7px 14px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.43;
  margin: 0px auto;
  cursor: pointer;
}

.address {
  font-family: 'Roboto', sans-serif;
  color: rgb(83, 83, 83);
  font-size: 14px;
  text-align: left;
  padding-left: 1rem;
  flex-grow: 1;
}

.chosen-address {
  font-family: 'Roboto', sans-serif;
  color: rgb(83, 83, 83);
  font-size: 14px;
  text-align: left;
  padding-left: 1rem;
  cursor: auto;
}

.location-list {
  text-align: center;
  background: rgb(255, 255, 255);
  padding-left: 0px;
  margin: 0px;
}

.fadeIn {
  animation: 300ms linear forwards fadeIn;
}

.label {
  font-weight: bold;
  font-size: 15px;
}

.location-button:hover {
  background-color: rgba(1, 19, 61, 0.094);
}

.cancel-button:hover {
  color: #d4d2d2;
}

.location-container {
  padding: 0.5rem 1.3rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  line-height: 1.4em;
  cursor: auto;
  color: rgb(83, 83, 83);
  position: relative;
  background: rgb(255, 255, 255);
}

.container {
  min-height: 100%;
  box-sizing: content-box;
  position: fixed;
  width: 320px;
  background-color: #f5f5f5;
  align-items: flex-end;
  overflow-y: auto;
}

.ratings h1 {
  font-size: 17px;
}

.expanded {
  height: 100vh;
}
</style>
